import { validateKeyFormat, type LicensablePlan } from './keys.js';
import { loadLicense, saveLicense, deleteLicense, type LicenseRecord } from './store.js';
import { Plan } from '../billing/entitlements.js';

const DEFAULT_VERIFY_URL = 'https://api.browserforge.dev/v1/license/verify';
const VERIFY_TIMEOUT_MS = 5000;

export interface LicenseServiceOptions {
  bfDir?: string;
  verifyUrl?: string;
}

export interface ActivateResult {
  success: boolean;
  plan?: LicensablePlan;
  error?: string;
}

export interface LicenseStatus {
  active: boolean;
  plan: Plan;
  /** Masked key (last 8 chars visible). Use for display only. */
  maskedKey?: string;
  activatedAt?: string;
  lastVerifiedAt?: string;
}

/** Mask a key, showing only the last 8 characters: BF-pro-****..****-ab12 → ****ab12 */
function maskKey(key: string): string {
  const visible = 8;
  if (key.length <= visible) return key;
  return '****' + key.slice(-visible);
}

export class LicenseService {
  private readonly bfDir?: string;
  private readonly verifyUrl: string;

  constructor(bfDirOrOpts?: string | LicenseServiceOptions) {
    if (typeof bfDirOrOpts === 'string' || bfDirOrOpts === undefined) {
      this.bfDir = bfDirOrOpts;
      this.verifyUrl = DEFAULT_VERIFY_URL;
    } else {
      this.bfDir = bfDirOrOpts.bfDir;
      this.verifyUrl = bfDirOrOpts.verifyUrl ?? DEFAULT_VERIFY_URL;
    }
  }

  /**
   * Activate a license key. Validates format locally, then optionally
   * phones home (graceful offline fallback). Saves to disk on success.
   */
  async activate(key: string): Promise<ActivateResult> {
    const validation = validateKeyFormat(key);
    if (!validation.valid || !validation.plan) {
      return { success: false, error: 'Invalid license key format or checksum' };
    }

    const plan = validation.plan;

    // Try online verification — but don't block on failure
    const onlineResult = await this.verifyOnline(key);
    if (onlineResult === false) {
      // Server responded and explicitly rejected the key
      return { success: false, error: 'License key has been revoked or is invalid' };
    }
    // onlineResult === true (valid) or null (offline/timeout) — trust local check

    const now = new Date().toISOString();
    const record: LicenseRecord = {
      key: key.toLowerCase(),
      plan,
      activatedAt: now,
      lastVerifiedAt: now,
    };

    saveLicense(record, this.bfDir);
    return { success: true, plan };
  }

  /** Remove the active license and revert to FREE plan. */
  deactivate(): void {
    deleteLicense(this.bfDir);
  }

  /**
   * Return the current license state. If a license is stored, validates
   * its format to guard against tampered files.
   */
  getStatus(): LicenseStatus {
    const record = loadLicense(this.bfDir);
    if (!record) {
      return { active: false, plan: Plan.FREE };
    }

    // Re-validate format — catches tampered files
    const validation = validateKeyFormat(record.key);
    if (!validation.valid || !validation.plan) {
      console.warn('[BrowserForge] Stored license key is invalid or tampered — removing and reverting to FREE plan');
      deleteLicense(this.bfDir);
      return { active: false, plan: Plan.FREE };
    }

    return {
      active: true,
      plan: record.plan,
      maskedKey: maskKey(record.key),
      activatedAt: record.activatedAt,
      lastVerifiedAt: record.lastVerifiedAt,
    };
  }

  /**
   * Hit the verification API. Returns:
   *   true  — key is valid according to server
   *   false — server explicitly rejected it (revoked/invalid)
   *   null  — couldn't reach server (offline/timeout) — fall back to local check
   */
  private async verifyOnline(key: string): Promise<boolean | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), VERIFY_TIMEOUT_MS);
    try {
      const res = await fetch(this.verifyUrl, {
        signal: controller.signal,
        headers: { 'Authorization': `Bearer ${key}` },
      });

      if (res.status === 200) return true;
      // Explicit rejection — key is revoked or unknown
      if (res.status === 402 || res.status === 403 || res.status === 404) return false;
      // 429, 5xx, or other transient errors → treat as offline
      return null;
    } catch {
      // Network error, ENOTFOUND, AbortError → offline fallback
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
}
